import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import toastHandler from 'Utils/toastHandler';
import {
  getThreadTitle,
  Thread,
  useDeleteThread,
  useGetThreads,
  useRenameThread
} from './Chat/hooks';
import { HistoryGroup, HistoryThread } from './components/Sidebar';
import { Specialist } from './Utils/specialists';

type HistoryGroupKey = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'older';

const GROUP_ORDER: HistoryGroupKey[] = [
  'today',
  'yesterday',
  'last7Days',
  'last30Days',
  'older'
];
const DAY_IN_MS = 1000 * 60 * 60 * 24;

const startOfDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

const getGroupKey = (createdAt: string, today: number): HistoryGroupKey => {
  const diffDays = Math.floor((today - startOfDay(new Date(createdAt))) / DAY_IN_MS);
  if (diffDays <= 0) return 'today';
  if (diffDays === 1) return 'yesterday';
  if (diffDays <= 7) return 'last7Days';
  if (diffDays <= 30) return 'last30Days';
  return 'older';
};

const normalize = (value: string) =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

export type AiChatView = 'chat' | 'assistant-library';

const useAiChatModal = (constructionSiteId: number) => {
  const { t } = useTranslation('activities');
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedThreadId, setSelectedThreadId] = useState<string | undefined>();
  const [currentView, setCurrentView] = useState<AiChatView>('chat');
  const [selectedAssistant, setSelectedAssistant] = useState<string | undefined>();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [renamingThreadId, setRenamingThreadId] = useState<string | undefined>();
  const [threadToDelete, setThreadToDelete] = useState<HistoryThread | undefined>();
  // Muda a cada "Novo chat" para remontar o chat e descartar qualquer estado local
  const [chatSessionKey, setChatSessionKey] = useState(0);

  const { data: threads, isLoading: isLoadingThreads } = useGetThreads(
    constructionSiteId
  );
  const { mutate: renameThread } = useRenameThread(constructionSiteId);
  const { mutate: deleteThread, isPending: isDeleting } = useDeleteThread(
    constructionSiteId
  );

  const groups = useMemo<HistoryGroup[]>(() => {
    if (!threads) return [];

    const today = startOfDay(new Date());
    const query = normalize(search.trim());
    const map = new Map<HistoryGroupKey, HistoryThread[]>();

    [...threads]
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      .forEach((thread: Thread) => {
        const title = getThreadTitle(thread);
        if (query && !normalize(title).includes(query)) return;

        const key = getGroupKey(thread.createdAt, today);
        const list = map.get(key) ?? [];
        list.push({ threadId: thread.threadId, title });
        map.set(key, list);
      });

    return GROUP_ORDER.filter((key) => map.has(key)).map((key) => ({
      key,
      label: t(`aiChat.historyGroups.${key}`),
      threads: map.get(key) ?? []
    }));
  }, [threads, search, t]);

  const selectedThreadTitle = useMemo(() => {
    const thread = threads?.find((item) => item.threadId === selectedThreadId);
    return thread ? getThreadTitle(thread) : undefined;
  }, [threads, selectedThreadId]);

  const startNewChat = useCallback(() => {
    setChatSessionKey((key) => key + 1);
    setSelectedThreadId(undefined);
    setSelectedAssistant(undefined);
    setCurrentView('chat');
  }, []);

  const selectThread = useCallback((threadId: string) => {
    setSelectedThreadId(threadId);
    setCurrentView('chat');
  }, []);

  const selectSpecialist = useCallback((specialist: Specialist) => {
    setChatSessionKey((key) => key + 1);
    setSelectedThreadId(undefined);
    setSelectedAssistant(specialist.assistant);
    setCurrentView('chat');
  }, []);

  const selectLibraryAssistant = useCallback((assistant: string) => {
    setChatSessionKey((key) => key + 1);
    setSelectedThreadId(undefined);
    setSelectedAssistant(assistant);
    setCurrentView('chat');
  }, []);

  const toggleLibrary = useCallback(() => {
    setCurrentView((view) =>
      view === 'assistant-library' ? 'chat' : 'assistant-library'
    );
  }, []);

  const toggleSearch = useCallback(() => {
    setIsSearchOpen((open) => !open);
    setSearch('');
  }, []);

  const submitRename = useCallback(
    (threadId: string, name: string) => {
      setRenamingThreadId(undefined);
      renameThread(
        { threadId, name },
        {
          onSuccess: () => toastHandler.showSuccess(t('aiChat.renameSuccess')),
          onError: () => toastHandler.showError(t('aiChat.renameError'))
        }
      );
    },
    [renameThread, t]
  );

  const confirmDelete = useCallback(() => {
    if (!threadToDelete) return;

    const { threadId } = threadToDelete;
    deleteThread(threadId, {
      onSuccess: () => {
        setThreadToDelete(undefined);
        if (threadId === selectedThreadId) {
          startNewChat();
        }
      },
      onError: () => toastHandler.showError(t('aiChat.deleteError'))
    });
  }, [deleteThread, selectedThreadId, startNewChat, t, threadToDelete]);

  return {
    isSidebarOpen,
    toggleSidebar: () => setIsSidebarOpen((open) => !open),
    isExpanded,
    toggleExpanded: () => setIsExpanded((expanded) => !expanded),
    collapse: () => setIsExpanded(false),
    chatSessionKey,
    selectedThreadId,
    setSelectedThreadId,
    selectedThreadTitle,
    currentView,
    selectedAssistant,
    groups,
    hasThreads: Boolean(threads?.length),
    isLoadingThreads,
    isSearchOpen,
    search,
    setSearch,
    toggleSearch,
    renamingThreadId,
    setRenamingThreadId,
    submitRename,
    threadToDelete,
    setThreadToDelete,
    confirmDelete,
    isDeleting,
    startNewChat,
    selectThread,
    selectSpecialist,
    selectLibraryAssistant,
    toggleLibrary
  };
};

export default useAiChatModal;
